import * as Astronomy from "astronomy-engine";
import type { Vec3 } from "./coords";
import tables from "../data/dwarf-tables.json";

export type DwarfId = "ceres" | "haumea" | "makemake" | "eris";
export const DWARF_IDS: readonly DwarfId[] = ["ceres", "haumea", "makemake", "eris"];

interface Tables { startJDTDB: number; stepDays: number; bodies: Record<DwarfId, { n: number; xyz: number[] }> }
const T = tables as unknown as Tables;

/** 6 点拉格朗日插值（等间距节点）。 */
export function lagrange6(y: ArrayLike<number>, idx: (k: number) => number, u: number): number {
  // 节点 k = -2..3，u ∈ [0,1)
  let sum = 0;
  for (let k = -2; k <= 3; k++) {
    let w = 1;
    for (let m = -2; m <= 3; m++) if (m !== k) w *= (u - m) / (k - m);
    sum += w * y[idx(k)]!;
  }
  return sum;
}

/** 日心黄道 J2000 位置（AU）。时间换算为 TT（≈TDB）后在预生成表上插值。 */
export function dwarfHelioEcliptic(id: DwarfId, date: Date): Vec3 {
  const jdTT = Astronomy.MakeTime(date).tt + 2451545.0;
  const pos = (jdTT - T.startJDTDB) / T.stepDays;
  const body = T.bodies[id];
  const i0 = Math.floor(pos);
  if (i0 - 2 < 0 || i0 + 3 > body.n - 1) throw new RangeError(`${id}: date outside table coverage`);
  const u = pos - i0;
  const at = (c: number) => lagrange6(body.xyz, (k) => (i0 + k) * 3 + c, u);
  return { x: at(0), y: at(1), z: at(2) };
}

/** 测试辅助：取表中第 100 个节点的时刻回代插值，返回与表值的最大偏差（AU）。 */
export function dwarfNodeCheck(id: DwarfId): number {
  const k = 100, body = T.bodies[id];
  const jdTT = T.startJDTDB + k * T.stepDays;
  const date = Astronomy.MakeTime(new Date(0)); // 仅为拿到 UT/TT 换算；下面反推 UT 日期
  void date;
  // TT → UT：迭代求使 MakeTime(date).tt + 2451545 = jdTT 的 date
  let ms = (jdTT - 2440587.5) * 86400000;
  for (let i = 0; i < 4; i++) ms -= (Astronomy.MakeTime(new Date(ms)).tt + 2451545.0 - jdTT) * 86400000;
  const v = dwarfHelioEcliptic(id, new Date(ms));
  const t = body.xyz;
  return Math.max(Math.abs(v.x - t[k * 3]!), Math.abs(v.y - t[k * 3 + 1]!), Math.abs(v.z - t[k * 3 + 2]!));
}
